import type { LocalRecord } from '@dovefiniscono/sync';
import type { Category, MoneyAccount, Transaction } from './summary';
import { depthOf } from './summary';

export type Invoice = LocalRecord<'invoices'>;
export type PlannedIncome = LocalRecord<'plannedIncomes'>;
export type Obligation = LocalRecord<'obligations'>;
export type PlannedPurchase = LocalRecord<'plannedPurchases'>;
export type FiscalYear = LocalRecord<'fiscalYears'>;

/** Share set aside for taxes and contributions when a year has no setting: 20%. */
export const DEFAULT_TAX_RATE = 2000;

const inYear = (date: string | null, year: number) => date?.startsWith(`${year}-`) ?? false;

/** Newest invoices first. */
export const byIssueDate = (a: Invoice, b: Invoice) =>
  b.issuedOn.localeCompare(a.issuedOn) || b.createdAt.localeCompare(a.createdAt);

/** Invoices of a year (by issue date): invoiced, collected, to collect, and by client. */
export function invoiceSummary(invoices: readonly Invoice[], year: number) {
  const ofYear = invoices.filter((invoice) => inYear(invoice.issuedOn, year));
  const clients = new Map<string, { client: string; cents: number; count: number }>();
  let invoiced = 0;
  for (const invoice of ofYear) {
    invoiced += invoice.amount;
    // "EUROSERVICE S.R.L." and "Euroservice S.r.l. " are the same client.
    const key = invoice.client.trim().toLowerCase().replace(/\s+/g, ' ');
    const entry = clients.get(key) ?? { client: invoice.client.trim(), cents: 0, count: 0 };
    entry.cents += invoice.amount;
    entry.count += 1;
    clients.set(key, entry);
  }
  return {
    invoices: [...ofYear].sort(byIssueDate),
    invoiced,
    /** Collected in this year, whatever year the invoice is from (cash basis). */
    collected: invoices
      .filter((invoice) => inYear(invoice.paidOn, year))
      .reduce((sum, invoice) => sum + invoice.amount, 0),
    /** Still to collect, from any year. */
    outstanding: invoices
      .filter((invoice) => invoice.paidOn === null)
      .reduce((sum, invoice) => sum + invoice.amount, 0),
    byClient: [...clients.values()].sort((a, b) => b.cents - a.cents),
  };
}

/** The tax rate of a year: its setting (the latest, if two devices created one), else 20%. */
export function taxRateFor(fiscalYears: readonly FiscalYear[], year: number): number {
  const settings = fiscalYears
    .filter((setting) => setting.year === year)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  return settings[0]?.taxRate ?? DEFAULT_TAX_RATE;
}

/**
 * What the year should bring: invoices already issued plus expected income, before and after the
 * share for taxes and contributions, and as a monthly pay over 12 or 13 months.
 */
export function yearForecast(
  invoices: readonly Invoice[],
  planned: readonly PlannedIncome[],
  year: number,
  taxRate: number,
) {
  const invoiced = invoices
    .filter((invoice) => inYear(invoice.issuedOn, year))
    .reduce((sum, invoice) => sum + invoice.amount, 0);
  const expected = planned
    .filter((item) => item.year === year)
    .reduce((sum, item) => sum + item.amount, 0);
  const gross = invoiced + expected;
  const taxes = Math.round((gross * taxRate) / 10_000);
  const net = gross - taxes;
  return {
    invoiced,
    expected,
    gross,
    taxes,
    net,
    monthlyNet12: Math.round(net / 12),
    monthlyNet13: Math.round(net / 13),
    monthlyGross13: Math.round(gross / 13),
  };
}

export const WEALTH_GROUPS = [
  { key: 'liquid', label: 'Liquidità', kinds: ['bank', 'card', 'cash'] },
  { key: 'savings', label: 'Risparmi', kinds: ['savings'] },
  { key: 'investments', label: 'Investimenti', kinds: ['investment'] },
] as const;

/** What you own, by group. Accounts paid by others are not yours and stay out. */
export function wealth(accounts: readonly MoneyAccount[], transactions: readonly Transaction[]) {
  const balances = new Map(accounts.map((account) => [account.id, account.openingBalance]));
  for (const transaction of transactions) {
    const current = balances.get(transaction.accountId);
    if (current !== undefined) balances.set(transaction.accountId, current + transaction.amount);
  }
  const groups = WEALTH_GROUPS.map((group) => {
    const members = accounts.filter((account) =>
      (group.kinds as readonly string[]).includes(account.kind),
    );
    return {
      key: group.key,
      label: group.label,
      accounts: members.map((account) => ({ account, balance: balances.get(account.id) ?? 0 })),
      total: members.reduce((sum, account) => sum + (balances.get(account.id) ?? 0), 0),
    };
  });
  const external = accounts
    .filter((account) => account.kind === 'external')
    .map((account) => ({
      account,
      // Their balance goes down as they pay for you: show it as what they paid.
      paid: account.openingBalance - (balances.get(account.id) ?? 0),
    }));
  return {
    groups,
    total: groups.reduce((sum, group) => sum + group.total, 0),
    /** What you can spend: liquidity and savings, investments excluded. */
    available: groups
      .filter((group) => group.key !== 'investments')
      .reduce((sum, group) => sum + group.total, 0),
    external,
  };
}

const byDueDate = (a: Obligation, b: Obligation) =>
  (a.dueOn ?? '9999').localeCompare(b.dueOn ?? '9999') || a.createdAt.localeCompare(b.createdAt);

/** Open debts and credits, the earliest due first. */
export function openObligations(obligations: readonly Obligation[]) {
  const open = obligations.filter((item) => item.settledOn === null).sort(byDueDate);
  const payables = open.filter((item) => item.direction === 'payable');
  const receivables = open.filter((item) => item.direction === 'receivable');
  return {
    payables,
    receivables,
    payable: payables.reduce((sum, item) => sum + item.amount, 0),
    receivable: receivables.reduce((sum, item) => sum + item.amount, 0),
    settled: obligations
      .filter((item) => item.settledOn !== null)
      .sort((a, b) => (b.settledOn ?? '').localeCompare(a.settledOn ?? '')),
  };
}

/** Planned purchases of a year: still to buy (necessary, optional, of which business) and bought. */
export function purchaseTotals(purchases: readonly PlannedPurchase[], year: number) {
  const ofYear = purchases.filter((purchase) => purchase.year === year);
  const toBuy = ofYear.filter((purchase) => purchase.purchasedOn === null);
  const sum = (list: readonly PlannedPurchase[]) => list.reduce((s, p) => s + p.price, 0);
  return {
    items: [...ofYear].sort(
      (a, b) =>
        Number(a.purchasedOn !== null) - Number(b.purchasedOn !== null) ||
        Number(b.necessary) - Number(a.necessary) ||
        b.price - a.price,
    ),
    necessary: sum(toBuy.filter((p) => p.necessary)),
    optional: sum(toBuy.filter((p) => !p.necessary)),
    business: sum(toBuy.filter((p) => p.business)),
    bought: sum(ofYear.filter((p) => p.purchasedOn !== null)),
  };
}

/** Still to buy first, then the earliest date, the necessary and the most expensive. */
const byPlan = (a: PlannedPurchase, b: PlannedPurchase) =>
  Number(a.purchasedOn !== null) - Number(b.purchasedOn !== null) ||
  (a.plannedOn ?? '9999').localeCompare(b.plannedOn ?? '9999') ||
  Number(b.necessary) - Number(a.necessary) ||
  b.price - a.price;

/**
 * Planned purchases of a year by category, in the order of the categories: each top category with
 * its items (those of its subcategories included) and what is still to buy. "Senza categoria" last.
 */
export function purchasesByCategory(
  purchases: readonly PlannedPurchase[],
  categories: readonly Category[],
  year: number,
) {
  const byId = new Map(categories.map((c) => [c.id, c]));
  const rootOf = (id: string | null | undefined) => {
    let category = id ? byId.get(id) : undefined;
    for (let steps = 0; category?.parentId && steps < 5; steps++) {
      const parent = byId.get(category.parentId);
      if (!parent) break;
      category = parent;
    }
    return category ?? null;
  };
  const groups = new Map<
    string,
    { category: Category | null; items: PlannedPurchase[]; toBuy: number }
  >();
  for (const purchase of purchases) {
    if (purchase.year !== year) continue;
    const root = rootOf(purchase.categoryId);
    const group = groups.get(root?.id ?? '') ?? { category: root, items: [], toBuy: 0 };
    group.items.push(purchase);
    if (purchase.purchasedOn === null) group.toBuy += purchase.price;
    groups.set(root?.id ?? '', group);
  }
  for (const group of groups.values()) group.items.sort(byPlan);
  return [...groups.values()].sort((a, b) => {
    if (!a.category) return 1;
    if (!b.category) return -1;
    return a.category.position - b.category.position;
  });
}

const wordsOf = (text: string) =>
  text
    .toLowerCase()
    .split(/[^\p{L}]+/u)
    .filter((word) => word.length >= 4);

/**
 * The category that shares a word with what is being paid ("Tasse 2026" → "Tasse e contributi"),
 * subcategories first; null when none does.
 */
export function guessCategory(text: string, categories: readonly Category[]): string | null {
  const words = new Set(wordsOf(text));
  if (words.size === 0) return null;
  // The deepest first: "sushi" goes to Cena fuori › Sushi, not to Cena.
  const parentOf = new Map(
    categories.flatMap((c) => (c.parentId ? [[c.id, c.parentId] as const] : [])),
  );
  const match = [...categories]
    .sort((a, b) => depthOf(b.id, parentOf) - depthOf(a.id, parentOf))
    .find((category) => wordsOf(category.name).some((word) => words.has(word)));
  return match?.id ?? null;
}

/**
 * "If from today you collected nothing more": what you own, plus what you are owed, minus what you
 * owe and the necessary purchases still planned (any year). The optional ones are shown apart.
 */
export function projection(input: {
  wealth: number;
  receivable: number;
  payable: number;
  purchases: readonly PlannedPurchase[];
}) {
  const toBuy = input.purchases.filter((purchase) => purchase.purchasedOn === null);
  const necessary = toBuy.filter((p) => p.necessary).reduce((sum, p) => sum + p.price, 0);
  const optional = toBuy.filter((p) => !p.necessary).reduce((sum, p) => sum + p.price, 0);
  const balance = input.wealth + input.receivable - input.payable - necessary;
  return { necessary, optional, balance, withOptional: balance - optional };
}
